import { Heading } from "@ariakit/react";
import {
  fold,
  idToDate,
  ListItemContainer,
  ListItemContent,
  ListItemHeading,
  ListItemSummary,
  ListView,
  MiddotSeparated,
  RichText,
  useAppConfig,
  useFetchJsonResult,
} from "@indietabletop/appkit";
import { Link, Route, Switch, useParams } from "wouter";
import { FailureView } from "../FailureView/FailureView.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import {
  Page,
  SectionContainer,
  SectionContent,
  SectionHeading,
  ViewContent,
} from "../oss/Layout/Layout.tsx";
import { doc, item } from "./style.css.ts";
import type { ChangelogItem, HelpItem, WithContent } from "./types.ts";

export function DocRoutes() {
  return (
    <Switch>
      <Route path="/">
        <DocPagesIndex />
      </Route>

      <Route path="/:dir/:id">
        <HelpPage />
      </Route>
    </Switch>
  );
}

function HelpPage() {
  const { fmt } = useAppConfig();
  const { id, dir } = useParams<{ id: string; dir: string }>();
  const result = useFetchJsonResult<WithContent<ChangelogItem | HelpItem>>(
    `/docs/${dir}/${id}.json`,
  );

  if (result.isPending) {
    return (
      <Page title="Docs" backHref="/">
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="Docs" backHref="/">
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const payload = result.value;
  return (
    <Page title="Docs" backHref="/">
      <ViewContent inlineVariant="narrow">
        <SectionContainer>
          <header className={doc.header}>
            <Heading className={doc.title}>{payload.data.title}</Heading>

            <MiddotSeparated className={doc.subheading}>
              {dir === "changelog" &&
                fmt.date(idToDate(id, () => new Date("2025-01-01")))}

              {payload.data.author}
            </MiddotSeparated>
          </header>

          <RichText trustedHtml={payload.content} />
        </SectionContainer>
      </ViewContent>
    </Page>
  );
}

function DocListItem(props: {
  href: string;
  heading: string;
  summary: string;
}) {
  return (
    <ListItemContainer>
      <ListItemContent
        render={<Link href={props.href} transition />}
        className={item.content}
      >
        <ListItemHeading className={item.heading}>
          {props.heading}
        </ListItemHeading>
        <ListItemSummary>{props.summary}</ListItemSummary>
      </ListItemContent>
    </ListItemContainer>
  );
}

function DocsIndexView(props: {
  changelog: ChangelogItem[];
  help: HelpItem[];
}) {
  const { fmt } = useAppConfig();
  const { help, changelog } = props;

  return (
    <ViewContent inlineVariant="narrow" blockVariant="shrink">
      <SectionContainer>
        <SectionHeading>Changelog</SectionHeading>
        <SectionContent>
          <ListView>
            {changelog.map((doc) => {
              const publishedDate = idToDate(
                doc.id,
                () => new Date("2026-01-01"),
              );

              return (
                <DocListItem
                  key={doc.id}
                  href={`/changelog/${doc.id}`}
                  heading={doc.data.title}
                  summary={fmt.date(publishedDate)}
                />
              );
            })}
          </ListView>
        </SectionContent>
      </SectionContainer>

      <SectionContainer>
        <SectionHeading>Help</SectionHeading>

        <SectionContent>
          <ListView>
            {help
              .map((doc) => {
                return (
                  <DocListItem
                    key={doc.id}
                    href={`/help/${doc.id}`}
                    heading={doc.data.title}
                    summary={doc.data.standalone}
                  />
                );
              })
              .reverse()}
          </ListView>
        </SectionContent>
      </SectionContainer>
    </ViewContent>
  );
}

function DocPagesIndex() {
  const result = fold([
    useFetchJsonResult<ChangelogItem[]>("/docs/changelog/index.json"),
    useFetchJsonResult<HelpItem[]>("/docs/help/index.json"),
  ]);

  if (result.isPending) {
    return (
      <Page title="Docs" backHref="~/">
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="Docs" backHref="~/">
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const [changelog, help] = result.value;
  return (
    <Page title="Docs" backHref="~/">
      <DocsIndexView changelog={changelog} help={help} />
    </Page>
  );
}
